/* eslint-disable no-bitwise -- mixing and folding bits is what a hash does;
   the operators are the algorithm, not a shortcut around arithmetic. */
/**
 * Folds a string into a 53-bit digest, rendered base36.
 *
 * One-directional and lossy by design: the input cannot be recovered from the
 * digest, so identifiers built from request payloads carry no readable
 * customer data. Not a cryptographic primitive (SubtleCrypto is async and
 * these identifiers are built synchronously), but an entire serialised body
 * collapses to 53 bits, leaving nothing legible to recover.
 *
 * Collision resistance is what matters for the callers: identical inputs
 * always produce the same digest, and unrelated payloads collide at a rate of
 * roughly one in nine quadrillion, well below the number of in-flight
 * requests a session ever holds.
 */
export const hashValue = (value: string): string => {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;

  for (let index = 0; index < value.length; index += 1) {
    const charCode = value.charCodeAt(index);
    h1 = Math.imul(h1 ^ charCode, 2654435761);
    h2 = Math.imul(h2 ^ charCode, 1597334677);
  }

  h1 =
    Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^
    Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 =
    Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^
    Math.imul(h1 ^ (h1 >>> 13), 3266489909);

  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
};
